import { useEffect } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { withUtm, captureUtms, captureVisit } from './lib/utm.js'
import { ThemeProvider } from './context/ThemeContext.jsx'
import { ToastProvider } from './context/ToastContext.jsx'
import { AuthProvider } from './context/AuthContext.jsx'
import { DataProvider } from './context/DataContext.jsx'
import { PeptidesProvider } from './context/PeptidesContext.jsx'
import { GlossaryProvider } from './context/GlossaryContext.jsx'
import { SiteProvider } from './context/SiteContext.jsx'
import Navbar from './components/Navbar.jsx'
import Seo from './components/Seo.jsx'
import ScrollToTop from './components/ScrollToTop.jsx'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import RequireAdmin from './components/RequireAdmin.jsx'
import { PEPTIDES } from './data/peptides.js'
import { WELLNESS_CALCS } from './data/wellness.js'
import Home from './pages/Home.jsx'
import Calculator from './pages/Calculator.jsx'
import Blend from './pages/Blend.jsx'
import Guide from './pages/Guide.jsx'
import DosageGuide from './pages/DosageGuide.jsx'
import Glossary from './pages/Glossary.jsx'
import GlossaryTerm from './pages/GlossaryTerm.jsx'
import AdminGlossary from './pages/AdminGlossary.jsx'
import AdminUtm from './pages/AdminUtm.jsx'
import AdminStats from './pages/AdminStats.jsx'
import { GLOSSARY } from './data/glossary.js'
import Guides from './pages/Guides.jsx'
import Articles from './pages/Articles.jsx'
import Article from './pages/Article.jsx'
import Sponsors from './pages/Sponsors.jsx'
import PeptideDosageChart from './pages/PeptideDosageChart.jsx'
import Feedback from './pages/Feedback.jsx'
import { ARTICLES } from './lib/articles.js'
import CoaGuide from './pages/CoaGuide.jsx'
import Stacks from './pages/Stacks.jsx'
import StackDetail from './pages/StackDetail.jsx'
import Charts from './pages/Charts.jsx'
import DosingChart from './pages/DosingChart.jsx'
import { CHART_SLUGS } from './lib/dosingChart.js'
import { STACKS } from './data/stacks.js'
import Peptides from './pages/Peptides.jsx'
import PeptideDetail from './pages/PeptideDetail.jsx'
import AdminPeptides from './pages/AdminPeptides.jsx'
import AdminHub from './pages/AdminHub.jsx'
import AdminBanners from './pages/AdminBanners.jsx'
import AdminEmails from './pages/AdminEmails.jsx'
import AdminArticles from './pages/AdminArticles.jsx'
import AdminArticlePreview from './pages/AdminArticlePreview.jsx'
import AdminEmailLog from './pages/AdminEmailLog.jsx'
import AdminIntegrations from './pages/AdminIntegrations.jsx'
import Wellness from './pages/Wellness.jsx'
import WellnessCalc from './pages/WellnessCalc.jsx'
import Terms from './pages/Terms.jsx'
import Privacy from './pages/Privacy.jsx'
import Settings from './pages/Settings.jsx'
import Unsubscribe from './pages/Unsubscribe.jsx'
import Confirm from './pages/Confirm.jsx'
import Saved from './pages/Saved.jsx'
import Calendar from './pages/Calendar.jsx'
import Login from './pages/Login.jsx'
import Register from './pages/Register.jsx'
import ForgotPassword from './pages/ForgotPassword.jsx'
import ResetPassword from './pages/ResetPassword.jsx'
import Embed from './pages/Embed.jsx'
import EmbedInfo from './pages/EmbedInfo.jsx'
import NotFound from './pages/NotFound.jsx'

// All context providers, shared by the main layout and the embed layout.
function AppProviders({ children }) {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <DataProvider>
            <PeptidesProvider>
              <GlossaryProvider>
                <SiteProvider>{children}</SiteProvider>
              </GlossaryProvider>
            </PeptidesProvider>
          </DataProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

// Root layout — providers + chrome wrap every page's <Outlet/>.
function Layout() {
  useEffect(() => { captureUtms(); captureVisit() }, [])
  return (
    <AppProviders>
      <div className="app">
        {/* Default head tags; per-page <Seo> overrides title/description. */}
        <Seo
          title="Peptide Reconstitution Calculator | CalculateMyPeptides"
          description="Free peptide reconstitution and dosing calculator. Enter your vial, bacteriostatic water, and dose to get the exact insulin-syringe units to draw, concentration, and doses per vial — plus a peptide education library and wellness calculators."
        />
        <ScrollToTop />
        <Navbar />
        <Outlet />
        <Footer />
      </div>
    </AppProviders>
  )
}

// Minimal layout for the /embed iframe target — providers, no site chrome.
function EmbedLayout() {
  return (
    <AppProviders>
      <div className="embed-root">
        <Outlet />
      </div>
    </AppProviders>
  )
}

export const routes = [
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'calculator', element: <Calculator /> },
      { path: 'blend', element: <Blend /> },
      { path: 'guide', element: <Guide /> },
      { path: 'peptide-dosage-guide', element: <DosageGuide /> },
      { path: 'peptide-dosage-chart', element: <PeptideDosageChart /> },
      { path: 'glossary', element: <Glossary /> },
      {
        path: 'glossary/:slug',
        element: <GlossaryTerm />,
        getStaticPaths: () => GLOSSARY.map((t) => `glossary/${t.slug}`),
      },
      { path: 'guides', element: <Guides /> },
      { path: 'how-to-read-a-coa', element: <CoaGuide /> },
      { path: 'sponsors', element: <Sponsors /> }, // UNLISTED: noindex + excluded from sitemap, linked nowhere
      { path: 'feedback', element: <Feedback /> },
      { path: 'articles', element: <Articles /> },
      {
        path: 'articles/:slug',
        element: <Article />,
        getStaticPaths: () => ARTICLES.map((a) => `articles/${a.slug}`),
      },
      { path: 'stacks', element: <Stacks /> },
      {
        path: 'stacks/:slug',
        element: <StackDetail />,
        getStaticPaths: () => STACKS.map((s) => `stacks/${s.slug}`),
      },
      { path: 'charts', element: <Charts /> },
      {
        path: 'charts/:slug',
        element: <DosingChart />,
        getStaticPaths: () => CHART_SLUGS.map((s) => `charts/${s}`),
      },
      { path: 'peptides', element: <Peptides /> },
      {
        path: 'peptides/:slug',
        element: <PeptideDetail />,
        getStaticPaths: () => PEPTIDES.map((p) => `peptides/${p.slug}`),
      },
      { path: 'wellness', element: <Wellness /> },
      {
        path: 'wellness/:slug',
        element: <WellnessCalc />,
        getStaticPaths: () => WELLNESS_CALCS.map((c) => `wellness/${c.slug}`),
      },
      { path: 'terms', element: <Terms /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'embed-calculator', element: <EmbedInfo /> },
      { path: 'unsubscribe', element: <Unsubscribe /> },
      { path: 'confirm', element: <Confirm /> },
      { path: 'login', element: <Login /> },
      { path: 'register', element: <Register /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      { path: 'settings', element: <ProtectedRoute feature="notification settings"><Settings /></ProtectedRoute> },
      { path: 'saved', element: <ProtectedRoute feature="My Calculations"><Saved /></ProtectedRoute> },
      { path: 'calendar', element: <ProtectedRoute feature="the Calendar"><Calendar /></ProtectedRoute> },
      { path: 'admin', element: <RequireAdmin><AdminHub /></RequireAdmin> },
      { path: 'admin/peptides', element: <AdminPeptides /> },
      { path: 'admin/banners', element: <RequireAdmin><AdminBanners /></RequireAdmin> },
      { path: 'admin/glossary', element: <RequireAdmin><AdminGlossary /></RequireAdmin> },
      { path: 'admin/utm', element: <RequireAdmin><AdminUtm /></RequireAdmin> },
      { path: 'admin/stats', element: <RequireAdmin><AdminStats /></RequireAdmin> },
      { path: 'admin/emails', element: <RequireAdmin><AdminEmails /></RequireAdmin> },
      { path: 'admin/articles', element: <RequireAdmin><AdminArticles /></RequireAdmin> },
      { path: 'admin/articles/:slug', element: <RequireAdmin><AdminArticlePreview /></RequireAdmin> },
      { path: 'admin/emails-log', element: <RequireAdmin><AdminEmailLog /></RequireAdmin> },
      { path: 'admin/integrations', element: <RequireAdmin><AdminIntegrations /></RequireAdmin> },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    // Chrome-less calculator for embedding in an <iframe> on other sites.
    path: '/embed',
    element: <EmbedLayout />,
    children: [{ index: true, element: <Embed /> }],
  },
]

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        <div className="footer-links">
          <Link to="/calculator">Calculator</Link>
          <Link to="/blend">Blend Calculator</Link>
          <Link to="/peptides">Peptide Education</Link>
          <Link to="/charts">Dosing Charts</Link>
          <Link to="/peptide-dosage-guide">Dosage Guide</Link>
          <Link to="/peptide-dosage-chart">Dosage Chart</Link>
          <Link to="/articles">Articles</Link>
          <Link to="/glossary">Glossary</Link>
          <Link to="/wellness">Wellness</Link>
          <Link to="/embed-calculator">Embed our calculator</Link>
          <Link to="/feedback">Feedback</Link>
          <a href={withUtm('https://peptideaffiliateprogram.com/')} target="_blank" rel="noopener">Peptide Affiliate Program</a>
          <Link to="/terms">Terms &amp; Conditions</Link>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
        <div className="footer-meta">
          <span className="muted small">
            © {new Date().getFullYear()} CalculateMyPeptides · Educational tool — not medical advice.
          </span>
          <span className="muted small">Your calculations are securely saved to your account.</span>
        </div>
        <p className="muted small" style={{ marginTop: 10, maxWidth: '70ch', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
          All product and brand names are trademarks of their respective owners and are used for identification
          only. CalculateMyPeptides is not affiliated with, endorsed by, or sponsored by any manufacturer.
        </p>
      </div>
    </footer>
  )
}
